import { FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { WizardDataFormContext } from "./WizardDataFormContext";
import { GenericDataFormContext, QuotationInfoContext, StepOneFormContext } from "../../../../shared/contexts";

import QuotationService from "../../../../../../../../shared/services/quotation";

import { GenerateAPQuotationServiceRequest, SelectOption } from "../../../../../../../../shared/interfaces";

export const WizardDataFormProvider: FC<PropsWithChildren> = ({ children }) => {

    const { 
        paymentMethodsList,
        setPaymentMethodsList, 
        paymentMethods,
        setPaymentMethods, 
        setIsLoadingPaymentMethods 
    } = useContext( GenericDataFormContext )

    const { clientSelected } = useContext( StepOneFormContext );

    const { quotationInfo } = useContext( QuotationInfoContext );

    const [ ageRangeList, setAgeRangeList ] = useState<Array<SelectOption>>([]);
    const [ isLoadingAgeRangeList, setIsLoadingAgeRangeList ] = useState<boolean>(true);
    const [ ageRangeMap, setAgeRangeMap ] = useState<Map<string, { edadMinima: string; edadMaxima: string }>>(new Map());
  
    const [ relationshipList, setRelationshipList ] = useState<Array<SelectOption>>([]);
    const [ isLoadingRelationshipList, setIsLoadingRelationshipList ] = useState<boolean>(true);

    const [ quotationForm, setQuotationForm ] = useState<GenerateAPQuotationServiceRequest>();

    const getAgeRangeDescriptionById = useCallback((id: string) => ageRangeList.find(el => String(el.value) === String(id))?.label, [ageRangeList]);

    const getRelationshipDescriptionById = useCallback((id: string) => relationshipList.find(el => String(el.value) === String(id))?.label, [relationshipList]);

    const getAgeRangeMinMaxById = useCallback((id: string) => {
        return ageRangeMap.get(String(id));
    }, [ageRangeMap]);

    const getAgeRangeListPerProduct = useCallback(async ( article: string, branch: string, producto: string ) => {
        try {
            const response = await QuotationService.getAgeRangeList(article, branch, producto);
        
            if ( response.data ) {
                const ageRangeList = response.data.rangosDeEdad.map(({ codigo, descripcion }) => ({ label: descripcion, value: codigo }));
                const ageRangeMap = new Map<string, { edadMinima: string; edadMaxima: string }>();
                
                response.data.rangosDeEdad.forEach(({ codigo, edadMinima, edadMaxima }) => {
                    ageRangeMap.set(String(codigo), { edadMinima, edadMaxima });
                });
                
                setAgeRangeList( ageRangeList );
                setAgeRangeMap( ageRangeMap );
                setIsLoadingAgeRangeList(false);
            }
        } catch (error) {}
    }, [ setAgeRangeList, setIsLoadingAgeRangeList ]);
    
    const getRelationshipsListPerProduct = useCallback(async ( article: string, branch: string, product: string ) => {
        try {
            const response = await QuotationService.getRelationshipListPerProduct( article, branch, product );
        
            if ( response.data ) {
                const relationships = response.data.parentescos.map(({ codigo, descripcion }) => ({ label: descripcion, value: codigo }));
                setRelationshipList( relationships );
                setIsLoadingRelationshipList(false);
            }
        } catch (error) {}
    }, [ setRelationshipList, setIsLoadingRelationshipList ]);

    const getPaymentMethodPerVigency = useCallback(async ( article: string, vigencyFrom: string, vigencyTo: string ) => {
        try {
            setIsLoadingPaymentMethods(true);
            const response = await QuotationService.getPaymentPlans( article, vigencyFrom, vigencyTo );
        
            if ( response.data?.formasDePago?.length ) {
                const paymentMethods: Array<SelectOption> = response.data.formasDePago.map(({ descripcion, nroPlanDePago, codigoFormaDePago }) => ({ label: descripcion, value: `${codigoFormaDePago}-${nroPlanDePago}` }))
                setPaymentMethodsList( response.data.formasDePago );
                setPaymentMethods( paymentMethods );
            }
        } catch (error) {
        } finally {
            setIsLoadingPaymentMethods(false);
        }
    }, [ setPaymentMethods, setPaymentMethodsList ])

    useEffect(() => {
        if( clientSelected ) {
            setQuotationForm({
                ...quotationForm!,
                cliente: clientSelected,
            });
        };
    }, [ clientSelected ]);

    useEffect(() => {
        if (quotationInfo) {
            setQuotationForm((prevState) => ({
                ...prevState,
                token: quotationInfo.token!,
                rama: quotationInfo.branch,
                articuloRamaSecuencia: quotationInfo.arse,
            }));
        }
    }, [quotationInfo]);

    useEffect(() => {
        if(
            !paymentMethodsList.length &&
            !paymentMethods.length &&
            quotationInfo?.article && 
            quotationForm?.vigenciaDesde && 
            quotationForm?.vigenciaHasta
        ) {
            getPaymentMethodPerVigency( quotationInfo?.article, quotationForm.vigenciaDesde, quotationForm.vigenciaHasta );
        }
        /* eslint-disable-next-line */
    }, [ quotationInfo, quotationForm, getPaymentMethodPerVigency, paymentMethodsList, paymentMethods ])
    
    const values = useMemo(() => ({
        ageRangeList,
        isLoadingAgeRangeList,
        getAgeRangeListPerProduct,
        getRelationshipsListPerProduct,
        relationshipList,
        isLoadingRelationshipList,
        quotationForm,
        setQuotationForm,
        getRelationshipDescriptionById,
        getAgeRangeDescriptionById,
        getAgeRangeMinMaxById,
        getPaymentMethodPerVigency,
    }), [
        ageRangeList,
        isLoadingAgeRangeList,
        getAgeRangeListPerProduct,
        getRelationshipsListPerProduct,
        relationshipList,
        isLoadingRelationshipList,
        quotationForm,
        setQuotationForm,
        getRelationshipDescriptionById,
        getAgeRangeDescriptionById,
        getAgeRangeMinMaxById,
        getPaymentMethodPerVigency,
    ]);

    return (
        <WizardDataFormContext.Provider value={ values }>              
            { children }
        </WizardDataFormContext.Provider>
    )
}